import type { ChatMessages } from "@storytell/cp-client";
import type { ParsedWebCitation } from "./citationUtils";
import { parseWebCitation } from "./citationUtils";

/**
 * Collection of web search results organized by tool ID for efficient lookups
 */
export interface WebSearchResultsCollection {
  byToolId: Map<string, ChatMessages.WebSearchResult>;
}

/**
 * Resolves a web citation to find the corresponding search result
 *
 * @param citation - The citation string (e.g., "web_toolexecution_abc123_22")
 * @param collection - The collection of web search results
 * @returns The matching WebSearchResultItem, or null if not found
 */
export const resolveWebCitation = (
  citation: string,
  collection: WebSearchResultsCollection | undefined,
): ChatMessages.WebSearchResultItem | null => {
  // Parse the citation to extract toolId and index
  const parsed = parseWebCitation(citation);
  if (!parsed || !collection) {
    return null;
  }

  // Find the WebSearchResult by toolId
  const webSearchResult = collection.byToolId.get(parsed.toolId);
  if (!webSearchResult?.results) {
    return null;
  }

  // Find the specific result by index (1-based indexing)
  const resultItem = webSearchResult.results.find((result) => result.index === parsed.index);

  return resultItem || null;
};

/**
 * Validates if a parsed citation can be resolved in the collection
 *
 * @param parsed - The parsed citation object
 * @param collection - The collection of web search results
 * @returns True if the citation can be resolved, false otherwise
 */
export const canResolveCitation = (
  parsed: ParsedWebCitation,
  collection: WebSearchResultsCollection | undefined,
): boolean => {
  if (!collection) return false;

  const webSearchResult = collection.byToolId.get(parsed.toolId);
  if (!webSearchResult?.results) return false;

  return webSearchResult.results.some((result) => result.index === parsed.index);
};

/**
 * Gets all available citation indices for a given tool ID
 *
 * @param toolId - The tool ID to get indices for
 * @param collection - The collection of web search results
 * @returns Array of available indices, or empty array if none found
 */
export const getAvailableIndices = (toolId: string, collection: WebSearchResultsCollection | undefined): number[] => {
  if (!collection) return [];

  const webSearchResult = collection.byToolId.get(toolId);
  if (!webSearchResult?.results) return [];

  return webSearchResult.results.map((result) => result.index).sort((a, b) => a - b);
};
